import { Code2, Database, FileText, Radar } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, cx, Empty, ErrorNote, Modal, Spinner, toast } from "../../components/ui";
import { api, useApi, type Me } from "../../lib/api";
import { activeOption, useConnections } from "../../lib/connections";
import { active, jobName, size, STATUS_LABEL, STATUS_TONE, TYPE_LABEL, when, type RadarJob } from "./types";

/** RADAR home (design §15.2, docs/radar-plan.md §3d): this tenant's jobs and the two ways to start one. */
export default function RadarHome({ me }: { me: Me }) {
  const roles = me.apps?.find((a) => a.id === "radar")?.roles ?? [];
  const { data, error, loading, reload } = useApi<{ jobs: RadarJob[] }>("/apps/radar/jobs");
  const conn = activeOption(useConnections().apps.radar);
  const [dialog, setDialog] = useState<"export" | "report" | null>(null);
  const navigate = useNavigate();
  // Jobs take seconds to minutes: refresh while any is still going.
  const going = data?.jobs.some(active);
  useEffect(() => {
    if (!going) return;
    const t = setInterval(() => void reload(), 5000);
    return () => clearInterval(t);
  }, [going, reload]);

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ink">RADAR</h1>
          <p className="text-sm text-muted">Compass exports and payroll variance reports for {me.tenant!.name}{conn ? `, on ${conn.name} (${conn.envLabel})` : ""}.</p>
        </div>
        <div className="flex gap-2">
          {roles.includes("sql") && (
            <Link to="/apps/radar/sql" className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-surface px-3 h-9 text-sm font-medium text-ink-2 hover:bg-surface-2">
              <Code2 className="size-4" aria-hidden /> SQL library
            </Link>
          )}
          {roles.includes("export") && (
            <Button onClick={() => setDialog("export")} disabled={!conn} title={conn ? undefined : "Choose a connection first"}>
              <Database className="size-4" aria-hidden /> New export
            </Button>
          )}
          {roles.includes("reports") && (
            <Button variant="primary" onClick={() => setDialog("report")} disabled={!conn} title={conn ? undefined : "Choose a connection first"}>
              <FileText className="size-4" aria-hidden /> New report
            </Button>
          )}
        </div>
      </div>
      {!conn && <p className="text-sm text-muted mb-4">Choose an Infor connection with the button at the top right.</p>}
      {error && <ErrorNote>{error}</ErrorNote>}
      {loading && !data ? (
        <Spinner />
      ) : !data?.jobs.length ? (
        <Empty icon={<Radar className="size-8" />} title="No jobs yet">
          Start a Compass export or a report. Jobs started by Job Automation runs show here too.
        </Empty>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-line bg-surface">
          <table className="w-full text-sm">
            <thead className="text-left text-muted border-b border-line">
              <tr>
                <th className="px-4 py-2 font-medium">Job</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Result</th>
                <th className="px-4 py-2 font-medium">Started</th>
              </tr>
            </thead>
            <tbody>
              {data.jobs.map((j) => (
                <tr key={j.jobId} className="border-b border-line last:border-0">
                  <td className="px-4 py-3">
                    <Link to={`/apps/radar/jobs/${j.jobId}`} className="font-medium text-accent-text hover:underline">
                      {jobName(j)}
                    </Link>
                    <div className="text-xs text-muted">
                      {TYPE_LABEL[j.type]} · {j.connId}
                      {j.queryId ? ` · query ${j.queryId}` : ""}
                      {j.runId && (
                        <>
                          {" · "}
                          <Link to={`/apps/ja/runs/${j.runId}`} className="hover:underline">
                            from a Job Automation run
                          </Link>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={cx("text-xs font-semibold rounded-full px-2 py-0.5", STATUS_TONE[j.status])}>{STATUS_LABEL[j.status]}</span>
                  </td>
                  <td className="px-4 py-3 text-xs text-muted">
                    {j.status === "COMPLETED" ? [j.rows !== undefined ? `${j.rows} rows` : null, size(j.outputBytes), j.push?.printManagerDocumentId ? "in Print Manager" : null].filter(Boolean).join(" · ") : j.status === "FAILED" ? <span className="text-bad line-clamp-2">{j.error}</span> : ""}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted">
                    {when(j.createdAt)}
                    <div>{j.by}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {dialog === "export" && <ExportDialog onClose={() => setDialog(null)} onStarted={(j) => navigate(`/apps/radar/jobs/${j.jobId}`)} />}
      {dialog === "report" && <ReportDialog exports={(data?.jobs ?? []).filter((j) => j.type === "COMPASS_EXPORT" && j.status === "COMPLETED")} onClose={() => setDialog(null)} onStarted={(j) => navigate(`/apps/radar/jobs/${j.jobId}`)} />}
    </div>
  );
}

const input = "w-full h-9 rounded-lg border border-line bg-surface px-3 text-sm";

/** The optional Print Manager push, shared by both dialogs (design §15.1). */
function PushFields({ v, set }: { v: { pushToInfor: boolean; reportName: string; distProfile: string }; set: (p: Partial<typeof v>) => void }) {
  return (
    <fieldset className="space-y-3 text-sm">
      <label className="flex items-start gap-2">
        <input type="checkbox" className="mt-1" checked={v.pushToInfor} onChange={(e) => set({ pushToInfor: e.target.checked })} />
        <span>
          <span className="font-medium text-ink">Send to Infor Print Manager</span>
          <span className="block text-xs text-muted">Shared with the connection's service account; optionally with a distribution group too.</span>
        </span>
      </label>
      <label className="block">
        <span className="text-ink-2 font-medium">Report name</span>
        <input className={input} value={v.reportName} onChange={(e) => set({ reportName: e.target.value })} placeholder="Optional" maxLength={200} />
      </label>
      {v.pushToInfor && (
        <label className="block">
          <span className="text-ink-2 font-medium">Distribution group</span>
          <input className={input} value={v.distProfile} onChange={(e) => set({ distProfile: e.target.value })} placeholder="Optional, e.g. EmployeeSnapshotGroup" maxLength={60} />
        </label>
      )}
    </fieldset>
  );
}

function ExportDialog({ onClose, onStarted }: { onClose: () => void; onStarted: (j: RadarJob) => void }) {
  const [v, setV] = useState({ queryId: "", pushToInfor: false, reportName: "", distProfile: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      const j = await api<RadarJob>("/apps/radar/exports", { body: { ...v, queryId: v.queryId.trim(), distProfile: v.pushToInfor ? v.distProfile : "" } });
      toast("Export started");
      onStarted(j);
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };
  return (
    <Modal
      title="New Compass export"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" busy={busy} disabled={!v.queryId.trim()} onClick={() => void start()}>
            Start export
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <ErrorNote>{error}</ErrorNote>}
        <label className="block text-sm">
          <span className="text-ink-2 font-medium">Compass query id</span>
          <input className={input} value={v.queryId} onChange={(e) => setV({ ...v, queryId: e.target.value })} placeholder="e.g. hfSDV2VSNkLkPDS4QvLcS" autoFocus />
          <span className="block text-xs text-muted">A query that has already finished. RADAR fetches its result; it doesn't run the query.</span>
        </label>
        <PushFields v={v} set={(p) => setV({ ...v, ...p })} />
      </div>
    </Modal>
  );
}

/** Uploads a CSV straight to the reports bucket with the API's 5-minute link (for files too big to send inline). */
async function upload(file: File): Promise<string> {
  const link = await api<{ csvKey: string; url: string; method: string; headers: Record<string, string> }>("/apps/radar/uploads", { body: {} });
  const res = await fetch(link.url, { method: link.method, headers: link.headers, body: file });
  if (!res.ok) throw new Error(`The upload failed (${res.status})`);
  return link.csvKey;
}
const INLINE_MAX = 900_000;

function ReportDialog({ exports, onClose, onStarted }: { exports: RadarJob[]; onClose: () => void; onStarted: (j: RadarJob) => void }) {
  const [reportType, setReportType] = useState<"timerecord" | "register">("timerecord");
  const [source, setSource] = useState<"export" | "file">(exports.length ? "export" : "file");
  const [fromJob, setFromJob] = useState(exports[0]?.jobId ?? "");
  const [file, setFile] = useState<File | null>(null);
  const [push, setPush] = useState({ pushToInfor: false, reportName: "", distProfile: "" });
  const [advanced, setAdvanced] = useState({ version: "", maxResponseTokens: "", temperature: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      const body: Record<string, unknown> = { reportType, ...push, distProfile: push.pushToInfor ? push.distProfile : "" };
      if (advanced.version.trim()) body.version = advanced.version.trim();
      if (advanced.maxResponseTokens) body.maxResponseTokens = Number(advanced.maxResponseTokens);
      if (advanced.temperature) body.temperature = Number(advanced.temperature);
      if (source === "export") body.fromJob = fromJob;
      else if (file) {
        if (file.size <= INLINE_MAX) body.csv = await file.text();
        else body.csvKey = await upload(file);
      }
      const j = await api<RadarJob>("/apps/radar/reports", { body });
      toast("Report started");
      onStarted(j);
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };
  const ready = source === "export" ? !!fromJob : !!file;
  return (
    <Modal
      title="New payroll variance report"
      onClose={onClose}
      wide
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" busy={busy} disabled={!ready} onClick={() => void start()}>
            Start report
          </Button>
        </>
      }
    >
      <div className="space-y-4 text-sm">
        {error && <ErrorNote>{error}</ErrorNote>}
        <fieldset>
          <legend className="text-ink-2 font-medium mb-1">Report</legend>
          <div className="flex gap-4">
            {(["timerecord", "register"] as const).map((t) => (
              <label key={t} className="flex items-center gap-2">
                <input type="radio" name="reportType" checked={reportType === t} onChange={() => setReportType(t)} />
                {t === "timerecord" ? "Time record (TIMERECORD)" : "Payroll register (REGISTER)"}
              </label>
            ))}
          </div>
        </fieldset>
        <fieldset className="space-y-2">
          <legend className="text-ink-2 font-medium mb-1">Payroll variance CSV</legend>
          <label className="flex items-center gap-2">
            <input type="radio" name="source" checked={source === "export"} disabled={!exports.length} onChange={() => setSource("export")} />
            From a finished Compass export
          </label>
          {source === "export" && (
            <select className={input} value={fromJob} onChange={(e) => setFromJob(e.target.value)}>
              {exports.map((j) => (
                <option key={j.jobId} value={j.jobId}>
                  {jobName(j)} · query {j.queryId} · {j.rows} rows · {when(j.createdAt)}
                </option>
              ))}
            </select>
          )}
          <label className="flex items-center gap-2">
            <input type="radio" name="source" checked={source === "file"} onChange={() => setSource("file")} />
            Upload a CSV file
          </label>
          {source === "file" && <input type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />}
          {!exports.length && <p className="text-xs text-muted">No finished Compass exports yet: upload the CSV instead.</p>}
        </fieldset>
        <PushFields v={push} set={(p) => setPush({ ...push, ...p })} />
        <details>
          <summary className="cursor-pointer text-ink-2 font-medium">Model settings for this report</summary>
          <p className="text-xs text-muted mt-1 mb-2">Leave blank to use your organization's RADAR settings.</p>
          <div className="grid grid-cols-3 gap-3">
            <label className="block">
              <span className="text-ink-2">Model version</span>
              <input className={input} value={advanced.version} onChange={(e) => setAdvanced({ ...advanced, version: e.target.value })} placeholder="claude-sonnet-4-6" />
            </label>
            <label className="block">
              <span className="text-ink-2">Max response tokens</span>
              <input className={input} type="number" min={1} max={64000} value={advanced.maxResponseTokens} onChange={(e) => setAdvanced({ ...advanced, maxResponseTokens: e.target.value })} />
            </label>
            <label className="block">
              <span className="text-ink-2">Temperature</span>
              <input className={input} type="number" min={0} max={1} step={0.05} value={advanced.temperature} onChange={(e) => setAdvanced({ ...advanced, temperature: e.target.value })} />
            </label>
          </div>
        </details>
      </div>
    </Modal>
  );
}
